// AppsFlyer OneLink for the React store buttons. A typed facade over
// `window.compassOneLink`, which public/onelink.js (loaded in index.html's
// <head>) publishes. That file owns the template, the Smart Script version and
// the parameter mapping; this one owns the types and the fallback.
import { useSyncExternalStore } from 'react'

interface CompassOneLink {
  url: () => string | null
  isOneLink: (href: string) => boolean
  subscribe: (listener: () => void) => () => void
}

declare global {
  interface Window {
    compassOneLink?: CompassOneLink
  }
}

function getOneLink(): CompassOneLink | undefined {
  if (typeof window === 'undefined') return undefined
  const api = window.compassOneLink
  return api && typeof api.url === 'function' && typeof api.subscribe === 'function'
    ? api
    : undefined
}

function readUrl(): string | null {
  try {
    return getOneLink()?.url() ?? null
  } catch {
    return null
  }
}

function subscribe(listener: () => void): () => void {
  const api = getOneLink()
  if (!api) return () => {}
  try {
    return api.subscribe(listener)
  } catch {
    return () => {}
  }
}

/**
 * The OneLink click URL for this visit, or null until Smart Script has built
 * one (or when it never will: blocked, failed, desktop, or a denied page).
 */
export function useOneLinkUrl(): string | null {
  return useSyncExternalStore(subscribe, readUrl, () => null)
}

/**
 * The href a store button should use: the OneLink when there is one, else the
 * direct store listing. OneLink only on a phone, where exactly one badge shows;
 * on desktop both badges keep their own listing.
 */
export function storeButtonHref(
  directUrl: string,
  oneLinkUrl: string | null,
  isMobile: boolean,
): string {
  return isMobile && oneLinkUrl ? oneLinkUrl : directUrl
}
